/**
 * Roadmap-3k §3 special level roles and §5/§9 city moments, derived on the client from the city
 * catalogs (a city's first and last level, its shops' ranges of 30) the way
 * tools/src/level-specs.ts derives them; the client never imports tools. From city order
 * ERA_ORDER (4) on:
 *
 *   - Grand Opening: the city's last level (a hard level, 3× coins): a celebration for the city,
 *     then the next city's card, or the end-of-content card after the last city;
 *   - shop finale: the last level of each shop's range (every 30th level), except the city's last
 *     level: a banner in the win flow pointing at the shop's next upgrade;
 *   - returning mechanic: a special the player already knows, back for the first time in a city
 *     (not on its intro, which the GP-07.1 intro covers): a one-card reminder before play.
 *
 * Each moment shows once, remembered in `flags.cityCards` (`open:<city>`, `finale:<shop>`,
 * `remind:<city>:<mechanic>`, `end`), like the other one-shot intros.
 */
import { cities, cityShops } from '../config/content.js';
import { has, t } from '../i18n/index.js';
import { levelId } from './progress.js';
import type { PlayerState } from './state.js';

/** First city order with the roadmap-3k roles (level-specs ERA_ORDER). */
export const ERA_ORDER = 4;
/** Levels per city: 5 shops × 30 (roadmap-3k §2; New York, Tokyo and Paris have the same shape). */
export const CITY_LEVELS = 150;

/**
 * The route (roadmap-3k §2): every city in progression order with its English name. The catalogs
 * are the truth (test/cityRoles.test.ts checks this list against every one of them); the list only
 * answers for a city whose pack is not on this device yet, e.g. the next city after a Grand Opening.
 */
export const ROUTE: readonly { id: string; name: string }[] = [
  { id: 'ny', name: 'New York' },
  { id: 'tokyo', name: 'Tokyo' },
  { id: 'paris', name: 'Paris' },
  { id: 'seoul', name: 'Seoul' },
  { id: 'mexico', name: 'Mexico City' },
  { id: 'london', name: 'London' },
  { id: 'mumbai', name: 'Mumbai' },
  { id: 'istanbul', name: 'Istanbul' },
  { id: 'rio', name: 'Rio de Janeiro' },
  { id: 'berlin', name: 'Berlin' },
  { id: 'bangkok', name: 'Bangkok' },
  { id: 'lagos', name: 'Lagos' },
  { id: 'shanghai', name: 'Shanghai' },
  { id: 'rome', name: 'Rome' },
  { id: 'buenosaires', name: 'Buenos Aires' },
  { id: 'jakarta', name: 'Jakarta' },
  { id: 'marrakech', name: 'Marrakech' },
  { id: 'sydney', name: 'Sydney' },
  { id: 'barcelona', name: 'Barcelona' },
  { id: 'cairo', name: 'Cairo' },
  { id: 'hanoi', name: 'Hanoi' },
  { id: 'lima', name: 'Lima' },
  { id: 'montreal', name: 'Montreal' },
];

/** Clutter and rush are not specials (level-specs SPECIALS): they never get a reminder. */
const NOT_SPECIAL = new Set(['clutter', 'rush']);
export const isSpecial = (m: string): boolean => !NOT_SPECIAL.has(m);

/** What the roles need to know about a city: its order, level range and shops. */
export interface CityShape {
  id: string;
  order: number;
  first: number;
  last: number;
  shops: { id: string; levels: readonly [number, number]; name?: string }[];
}

/** A city's shape from a catalog (its shops sorted by their first level). */
export function shapeOf(city: { id: string; order: number }, shops: readonly { id: string; levels: readonly [number, number]; name?: string }[]): CityShape {
  const own = [...shops].sort((a, b) => a.levels[0] - b.levels[0]);
  return { id: city.id, order: city.order, first: Math.min(...own.map((s) => s.levels[0])), last: Math.max(...own.map((s) => s.levels[1])), shops: own };
}

/** A city on this device (built in, or a registered pack), from its catalog; null when unknown. */
export function cityShape(id: string): CityShape | null {
  const c = cities.find((x) => x.id === id);
  const shops = cityShops(id);
  return c && shops.length ? shapeOf(c, shops) : null;
}

/** A route city's order: its catalog's, else its place on the route (0 when neither knows it). */
export function orderOf(id: string): number {
  return cities.find((c) => c.id === id)?.order ?? ROUTE.findIndex((r) => r.id === id) + 1;
}

/** A city's last level: its catalog's, else the route's (every city is CITY_LEVELS long). */
export function lastLevelOf(id: string): number {
  return cityShape(id)?.last ?? orderOf(id) * CITY_LEVELS;
}

/** The city's Grand Opening: its last level, from ERA_ORDER on. */
export function isGrandOpening(c: CityShape, n: number): boolean {
  return c.order >= ERA_ORDER && n === c.last;
}

/** The shop whose finale level n is (the last level of its range, from ERA_ORDER on, never the Grand Opening), else null. */
export function shopFinale(c: CityShape, n: number): string | null {
  if (c.order < ERA_ORDER || n === c.last) return null;
  return c.shops.find((s) => s.levels[1] === n)?.id ?? null;
}

/** Every finale level of a city, in order. */
export function finaleLevels(c: CityShape): number[] {
  return c.shops.map((s) => s.levels[1]).filter((n) => shopFinale(c, n) !== null);
}

/** The first level each special appears on in a city (its pack index), in level order. */
export function firstAppearances(entries: readonly { number: number; mechanics: readonly string[] }[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const e of [...entries].sort((a, b) => a.number - b.number)) for (const m of e.mechanics) if (isSpecial(m) && !out.has(m)) out.set(m, e.number);
  return out;
}

/**
 * The reminder levels of a city for a player who knew `known` when they arrived: each known
 * special's first level in the city (from ERA_ORDER on). A special they do not know yet gets
 * the full GP-07.1 intro on its first level instead (its intro level), never a reminder.
 */
export function reminderLevels(c: CityShape, entries: readonly { number: number; mechanics: readonly string[] }[], known: ReadonlySet<string>): Map<string, number> {
  if (c.order < ERA_ORDER) return new Map();
  return new Map([...firstAppearances(entries)].filter(([m]) => known.has(m)));
}

// ---------------------------------------------------------------- one-shot cards

export const cardSeen = (s: PlayerState, key: string): boolean => (s.flags.cityCards ?? []).includes(key);

/** Remember a card as shown (idempotent); true when it was new. */
export function markCard(s: PlayerState, key: string): boolean {
  if (cardSeen(s, key)) return false;
  s.flags.cityCards = [...(s.flags.cityCards ?? []), key];
  return true;
}

export const remindKey = (city: string, m: string) => `remind:${city}:${m}`;

/**
 * The reminders due on a campaign level before play: specials of the level the player already
 * knows (`seen`, the GP-07.1 list) that have not been reminded in this city yet, from ERA_ORDER on.
 */
export function remindersDue(s: PlayerState, city: string, order: number, mechanics: readonly string[]): string[] {
  if (order < ERA_ORDER) return [];
  return mechanics.filter((m) => isSpecial(m) && s.flags.seenMechanics.includes(m) && !cardSeen(s, remindKey(city, m)));
}

/** The city's last level is beaten. */
export const cityDone = (s: PlayerState, id: string): boolean => (s.levels[levelId(id, lastLevelOf(id))]?.bestStars ?? 0) > 0;

/** Every city on the route (and any other city this device knows) is done: the end of content. */
export function everyCityDone(s: PlayerState): boolean {
  return routeIds().every((id) => cityDone(s, id));
}

/** Route cities plus any other city on this device, in progression order. */
function routeIds(): string[] {
  const ids = new Set([...ROUTE.map((r) => r.id), ...cities.map((c) => c.id)]);
  return [...ids].sort((a, b) => orderOf(a) - orderOf(b));
}

/**
 * The city the next-city card points to after `city`'s Grand Opening: the next city along the
 * route not done yet; past the last city, the first one not done (packs play in any order).
 * Null when every city is done: the end-of-content card shows instead.
 */
export function nextCity(s: PlayerState, city: string): string | null {
  const ids = routeIds();
  const at = ids.indexOf(city);
  const open = (id: string) => id !== city && !cityDone(s, id);
  return ids.slice(at + 1).find(open) ?? ids.find(open) ?? null;
}

/** A city's English name when its catalog is not on this device (the route's). */
export const routeName = (id: string): string | undefined => ROUTE.find((r) => r.id === id)?.name;

/**
 * A city's name as the map shows it (MapScene `cityName`: its translation, else its catalog's),
 * else the route's, for the city cards, which load without the scenes (and Phaser).
 */
export function cityTitle(id: string): string {
  return has(`city.${id}.name`) ? t(`city.${id}.name`) : (cities.find((c) => c.id === id)?.name ?? routeName(id) ?? id);
}

/**
 * "Replay for 3 stars": the first level under 3 stars in the first city (route order) on this
 * device that has one; null when every level of every city here has 3 stars.
 */
export function replayTarget(s: PlayerState): { city: string; level: number } | null {
  for (const id of routeIds()) {
    const c = cityShape(id);
    if (!c) continue;
    for (let n = c.first; n <= c.last; n++) if ((s.levels[levelId(id, n)]?.bestStars ?? 0) < 3) return { city: id, level: n };
  }
  return null;
}

/** What the city moments showed this session (test hooks: cityOpening, finaleBanner, reminder, endOfContent). */
export const shownCards: {
  /** `lock`: the next city's card drew its teaser street's padlock (only with a next city) */
  cityOpening: { city: string; next: string | null; coins: number; lock?: boolean } | null;
  finaleBanner: { shop: string; level: number } | null;
  reminder: { city: string; level: number; mechanics: string[] } | null;
  endOfContent: { from: string; replay: { city: string; level: number } | null; events: boolean } | null;
} = { cityOpening: null, finaleBanner: null, reminder: null, endOfContent: null };
